import React, {useState, useEffect, useRef, useCallback} from 'react';

import type {User} from '../../pages/reportBuilder/constants/ReportBuilderInterfce';
import type {MentionMeta} from '../../pages/reportBuilder/utils/commentMentionTokens';
import {insertMentionAtCursor} from '../../pages/reportBuilder/utils/commentMentionTokens';
import getReportBuilderUsers from '../../services/modules/User/getReportBuilderUsers';
import {useReportUserStore} from '../../store/reportStore/reportUserStore';

const MENTION_PAGE_SIZE = 10;
const MENTION_SEARCH_DEBOUNCE_MS = 500;
/** In-progress @mention: @ immediately before cursor, no whitespace in query. */
const ACTIVE_MENTION_REGEX = /@([^\s@[]*)$/;

const matchesMentionQuery = (user: User, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  const searchableValues = [
    user.name,
    user.firstName,
    user.lastName,
    user.email
  ].filter(Boolean) as string[];

  return searchableValues.some((value) =>
    value.toLowerCase().includes(normalizedQuery)
  );
};

const normalizeMentionUser = (rawUser: any): User | null => {
  const id = String(rawUser?._id ?? rawUser?.id ?? '');
  if (!id) return null;
  const firstName = String(rawUser?.firstName ?? rawUser?.userFirstName ?? '');
  const lastName = String(rawUser?.lastName ?? rawUser?.userLastName ?? '');
  const nameFromParts = `${firstName} ${lastName}`.trim();
  const name = String(rawUser?.name ?? nameFromParts ?? '').trim();
  const getInitials = (n: string): string => {
    const parts = n.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0][0].toUpperCase();

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  return {
    id,
    name: name || 'Unknown User',
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    email: rawUser?.email ?? '',
    avatar: getInitials(name || rawUser?.email || ''),
    color: '#1976D2'
  };
};

const extractMentionPage = (
  payload: any,
  page: number
): {rawUsers: any[]; hasMore: boolean} => {
  if (Array.isArray(payload)) {
    return {
      rawUsers: payload,
      hasMore: payload.length >= MENTION_PAGE_SIZE
    };
  }

  const rawUsers = Array.isArray(payload?.users)
    ? payload.users
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.docs)
            ? payload.docs
            : [];

  const pagination = payload?.pagination ?? payload?.meta ?? {};
  const hasMoreFlag =
    payload?.hasMore ??
    payload?.hasNextPage ??
    pagination?.hasMore ??
    pagination?.hasNextPage;
  if (typeof hasMoreFlag === 'boolean') {
    return {rawUsers, hasMore: hasMoreFlag};
  }

  const totalPages =
    Number(payload?.totalPages ?? pagination?.totalPages) || undefined;
  if (totalPages) {
    return {rawUsers, hasMore: page < totalPages};
  }

  const nextPage = payload?.nextPage ?? pagination?.nextPage;
  if (typeof nextPage === 'number') {
    return {rawUsers, hasMore: nextPage > page};
  }

  return {
    rawUsers,
    hasMore: rawUsers.length >= MENTION_PAGE_SIZE
  };
};

export interface UseMentionPickerResult {
  mentionQuery: string;
  showMentions: boolean;
  mentionIndex: number;
  mentionPosition: {top: number; left: number};
  mentionUsers: User[];
  mentionHasMore: boolean;
  mentionLoading: boolean;
  mentionHasSearched: boolean;
  handleInputChange: (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    setText: (v: string) => void
  ) => void;
  handleMentionSelect: (
    selectedUser: User,
    text: string,
    setText: (v: string) => void,
    setMentions: React.Dispatch<React.SetStateAction<MentionMeta[]>>,
    textareaRef: React.RefObject<HTMLTextAreaElement | null>
  ) => void;
  handleMentionKeyDown: (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    onSubmit?: () => void
  ) => boolean;
  closeMentions: () => void;
  loadMoreMentions: () => void;
}

export const useMentionPicker = (): UseMentionPickerResult => {
  const users = useReportUserStore((s) => s.users);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionPosition, setMentionPosition] = useState({top: 0, left: 0});
  const [mentionUsers, setMentionUsers] = useState<User[]>([]);
  const [mentionPage, setMentionPage] = useState(1);
  const [mentionHasMore, setMentionHasMore] = useState(false);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionHasSearched, setMentionHasSearched] = useState(false);
  const mentionRequestRef = useRef(0);

  const loadMentionUsers = useCallback(
    async (search: string, page: number, append: boolean) => {
      const requestId = mentionRequestRef.current + 1;
      mentionRequestRef.current = requestId;
      if (!append) {
        setMentionUsers([]);
        setMentionPage(1);
        setMentionHasMore(false);
      }
      setMentionLoading(true);

      try {
        const payload = await getReportBuilderUsers(search, {
          page,
          limit: MENTION_PAGE_SIZE
        });
        if (requestId !== mentionRequestRef.current) return;

        const {rawUsers, hasMore} = extractMentionPage(payload, page);
        const nextUsers = rawUsers
          .map(normalizeMentionUser)
          .filter((user): user is User => Boolean(user));

        setMentionUsers((prev) => {
          if (!append) return nextUsers;
          const merged = [...prev, ...nextUsers];
          const byId = new Map(merged.map((user) => [user.id, user]));

          return Array.from(byId.values());
        });
        setMentionPage(page);
        setMentionHasMore(hasMore);
      } catch {
        if (!append) {
          setMentionUsers(users.filter((u) => matchesMentionQuery(u, search)));
          setMentionHasMore(false);
        }
      } finally {
        if (requestId === mentionRequestRef.current) {
          setMentionLoading(false);
          if (!append) {
            setMentionHasSearched(true);
          }
        }
      }
    },
    [users]
  );

  useEffect(() => {
    if (!showMentions) return;

    setMentionHasSearched(false);
    setMentionLoading(true);

    const timer = setTimeout(() => {
      void loadMentionUsers(mentionQuery, 1, false);
    }, MENTION_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [showMentions, mentionQuery, loadMentionUsers]);

  useEffect(() => {
    if (mentionIndex >= mentionUsers.length && mentionUsers.length > 0) {
      setMentionIndex(0);
    }
  }, [mentionIndex, mentionUsers.length]);

  const closeMentions = useCallback(() => {
    mentionRequestRef.current += 1;
    setShowMentions(false);
    setMentionIndex(0);
    setMentionUsers([]);
    setMentionPage(1);
    setMentionHasMore(false);
    setMentionLoading(false);
    setMentionHasSearched(false);
  }, []);

  const loadMoreMentions = useCallback(() => {
    if (!showMentions || mentionLoading || !mentionHasMore) return;
    void loadMentionUsers(mentionQuery, mentionPage + 1, true);
  }, [
    showMentions,
    mentionLoading,
    mentionHasMore,
    mentionQuery,
    mentionPage,
    loadMentionUsers
  ]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>, setText: (v: string) => void) => {
      const value = e.target.value;
      setText(value);
      const cursorPosition = e.target.selectionStart;
      const textBeforeCursor = value.slice(0, cursorPosition);
      const match = textBeforeCursor.match(ACTIVE_MENTION_REGEX);

      if (match) {
        setMentionQuery(match[1]);
        setShowMentions(true);
        setMentionIndex(0);
        const rect = e.target.getBoundingClientRect();
        setMentionPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX
        });
      } else {
        setShowMentions(false);
      }
    },
    []
  );

  const handleMentionSelect = useCallback(
    (
      selectedUser: User,
      text: string,
      setText: (v: string) => void,
      setMentions: React.Dispatch<React.SetStateAction<MentionMeta[]>>,
      textareaRef: React.RefObject<HTMLTextAreaElement | null>
    ) => {
      if (!textareaRef.current) return;
      const cursorPosition = textareaRef.current.selectionStart;
      const {text: newText, cursorAfter} = insertMentionAtCursor(
        text,
        cursorPosition,
        selectedUser.name
      );
      setText(newText);
      closeMentions();
      setMentions((prev) => {
        if (prev.some((m) => m.id === selectedUser.id)) return prev;

        return [
          ...prev,
          {
            id: selectedUser.id,
            name: selectedUser.name,
            email: selectedUser.email
          }
        ];
      });
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(cursorAfter, cursorAfter);
        }
      }, 0);
    },
    [closeMentions]
  );

  const handleMentionKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLTextAreaElement>,
      onSubmit?: () => void
    ): boolean => {
      if (showMentions && mentionUsers.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex((prev) => {
            const isAtLastItem = prev >= mentionUsers.length - 1;
            if (isAtLastItem && mentionHasMore && !mentionLoading) {
              loadMoreMentions();
            }

            return isAtLastItem ? prev : prev + 1;
          });

          return true;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex((prev) => Math.max(0, prev - 1));

          return true;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();

          return true;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          closeMentions();

          return true;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit();

        return true;
      }

      return false;
    },
    [
      showMentions,
      mentionUsers,
      mentionHasMore,
      mentionLoading,
      loadMoreMentions,
      closeMentions
    ]
  );

  return {
    mentionQuery,
    showMentions,
    mentionIndex,
    mentionPosition,
    mentionUsers,
    mentionHasMore,
    mentionLoading,
    mentionHasSearched,
    handleInputChange,
    handleMentionSelect,
    handleMentionKeyDown,
    closeMentions,
    loadMoreMentions
  };
};
